
// src/components/RealDriverSafetyScorecard.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';

interface Props {
  engineHealthData: any[];
}

const RealDriverSafetyScorecard: React.FC<Props> = ({ engineHealthData }) => {
  if (!engineHealthData || engineHealthData.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary">
          Driver behavior tracking coming soon
        </Typography>
      </Box>
    );
  }

  // Derive safety score from engine health (simplified)
  const avgHealthScore = Math.round(
    engineHealthData.reduce((sum, v) => sum + v.healthScore, 0) / engineHealthData.length
  );

  // Distribution based on health status
  const distribution = engineHealthData.reduce((acc, v) => {
    if (v.healthScore >= 80) acc.healthy++;
    else if (v.healthScore >= 60) acc.warning++;
    else acc.critical++;
    return acc;
  }, { healthy: 0, warning: 0, critical: 0 });

  const chartData = [
    { name: 'Healthy', value: distribution.healthy, color: '#4caf50' },
    { name: 'Warning', value: distribution.warning, color: '#ff9800' },
    { name: 'Critical', value: distribution.critical, color: '#f44336' }
  ].filter(item => item.value > 0);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
        {avgHealthScore}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Average Vehicle Health
      </Typography>
      
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Legend 
            verticalAlign="bottom" 
            height={36}
            formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
          />
        </PieChart>
      </ResponsiveContainer>
      
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        Based on {engineHealthData.length} vehicles
      </Typography>
    </Box>
  );
};

export default RealDriverSafetyScorecard;